/**
 * @copyright (C) 2023, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * SoonKi Min (skmin@rsupport.com)
 *
 * @description
 *
 * @created at Thu Jun 15 2023
 **/
/* eslint-disable @typescript-eslint/no-unused-vars */

import { createContext, useCallback, useContext, useMemo, useState, useEffect } from "react";
import { debounce } from "lodash-es";
import { useQuery } from "@tanstack/react-query";

import { useChatMessageActions, useSpaceChannelState, hasNewMessageInSpaceChat } from "@/frameworks/core/chat";
import { EventID } from "@/frameworks/core/commons";
import { SceneType } from "@/frameworks/libraries/commons";
import { Properties } from "@/frameworks/libraries/properties";
import { EventHandler } from "@rfice/eventhandler";
import { useMyRoom, useSpace } from "@/frameworks/core/API";
import { useMeeting } from "@/frameworks/core/API";
import { usePersistCurrentSpace, usePersistSpace, usePersistUser } from "@/frameworks/core/persistence";

import { S, ChatRoomList } from "./private";
import { MessageInput, MessageList } from "../../../Common";
import { Maybe } from "@/frameworks/libraries/components";
import { Configure } from "@/frameworks/libraries/utilities";
import { useRoomViewActions } from "@/frameworks/core/hooks";

export const SpaceChatContext = createContext<{ spaceType: "Space" | "DM" | "CONFERENCE" }>({ spaceType: "Space" });

export default function SpaceChat({
	spaceInfo,
	sceneType
}: {
	sceneType: SceneType;
	spaceInfo: IPC.Spaces.EnterSpace;
}) {
	const spaceAPI = useSpace();
	const myRoomAPI = useMyRoom();
	const meetingAPI = useMeeting();

	const [isShowMarkImage, setIsShowMarkImage] = useState(false);

	const spaceType = useMemo(
		() => (spaceInfo?.meetingType === "CONFERENCE" ? spaceInfo.meetingType : "Space"),
		[spaceInfo]
	);

	const { data, isLoading, isFetching } = useQuery({
		queryKey: ["spaceInfo", spaceInfo.spaceID, spaceInfo.sceneType],
		queryFn: async () => {
			if (spaceInfo.sceneType === SceneType.MyRoomScene) {
				return (await myRoomAPI.inquiry.execute({ myRoomId: spaceInfo.spaceID })).data;
			} else if (spaceInfo.meetingType === "CONFERENCE") {
				return await meetingAPI.conference.detail({ conferenceId: spaceInfo.spaceID });
			} else {
				return (await spaceAPI.detail.execute({ spaceId: spaceInfo.spaceID })).data;
			}
		},
		refetchOnWindowFocus: false
	});
	if (!data || isLoading || isFetching) return;

	return (
		<SpaceChatContext.Provider value={{ spaceType }}>
			<S.Layout id="chat-layout" gap={4} reverse verticalAlign="bottom" isShowMarkImage={isShowMarkImage}>
				{sceneType === SceneType.OpenWorldScene ? (
					<OpenWorld
						spaceID={spaceInfo.spaceID}
						generalRoomID={data.generalRoomId || ""}
						openWorldName={data.name}
						onChangeShowMarkImage={(isShow) => {
							setIsShowMarkImage(isShow);
						}}
					/>
				) : spaceInfo?.meetingType === "CONFERENCE" ? (
					<Conference
						spaceID={spaceInfo.spaceID}
						spaceInfo={spaceInfo}
						generalRoomID={data.chatRoomId || ""}
						onChangeShowMarkImage={(isShow) => {
							setIsShowMarkImage(isShow);
						}}
					/>
				) : (
					<Space
						spaceID={spaceInfo.spaceID}
						spaceInfo={spaceInfo}
						generalRoomID={data.generalRoomId || ""}
						onChangeShowMarkImage={(isShow) => {
							setIsShowMarkImage(isShow);
						}}
					/>
				)}
			</S.Layout>
		</SpaceChatContext.Provider>
	);
}

function Space({
	spaceID,
	generalRoomID,
	spaceInfo,
	onChangeShowMarkImage
}: {
	spaceID: string;
	generalRoomID: string;
	spaceInfo: IPC.Spaces.EnterSpace;
	onChangeShowMarkImage?: (isShow: boolean) => void;
}) {
	const channelInfo = useSpaceChannelState(spaceID);
	const [currentChatRoomID, setCurrentChatRoomID] = useState(generalRoomID);
	const [isMinimizeSpaceChat, setIsMinimizeSpaceChat] = useState(false);
	const { messageReaded } = useChatMessageActions();
	const me = usePersistUser("me");
	const hasNewMessage = channelInfo && hasNewMessageInSpaceChat(channelInfo);
	const spaceChathatContext = useContext(SpaceChatContext);
	const meetingAPI = useMeeting();
	const { updateRoomView } = useRoomViewActions();

	useEffect(() => {
		updateRoomView({
			roomId: currentChatRoomID,
			deviceKey: Properties.browserTabID,
			view: true
		});
		return () => {
			updateRoomView({
				roomId: currentChatRoomID,
				deviceKey: Properties.browserTabID,
				view: false
			});
		};
	}, [currentChatRoomID]);

	const currentRoom = channelInfo?.rooms.find((room) => room.roomId === currentChatRoomID);

	const handleShowRecentMessage = useCallback(
		debounce((message) => {
			if (message.roomMessageId === currentRoom?.messageLastReadId || !currentRoom) return;
			if (message.isMe) return;
			messageReaded({ channelID: currentChatRoomID, messageID: message.roomMessageId });
		}, 1000),
		[currentChatRoomID, !currentRoom, spaceID, currentRoom?.messageLastReadId]
	);

	const chatUserList = currentRoom?.users.map((user) => ({
		name: user.name,
		src: user.profileImagePath,
		userID: user.id
	}));

	const handleDrop = (ev: React.DragEvent<HTMLLabelElement>) => {
		EventHandler.emit(EventID.DROP_CHAT_FILES, {
			channelID: currentChatRoomID,
			fileList: [...ev.dataTransfer.files],
			variant: "transparent"
		});
	};

	const saveUploadActions = useCallback(() => {
		if (spaceChathatContext.spaceType === "CONFERENCE") {
			meetingAPI.actionLog.save({
				conferenceId: spaceInfo.spaceID || "",
				actionType: "UPLOAD"
			});
		}
	}, [spaceChathatContext.spaceType]);

	return (
		<>
			<ChatRoomList
				isOpenWorld={false}
				spaceInfo={spaceInfo}
				channelInfo={channelInfo}
				generalRoomID={generalRoomID}
				onSelectChannel={setCurrentChatRoomID}
				toggleMinimizeSpaceChat={() =>
					setIsMinimizeSpaceChat((val) => {
						if (!val) {
							onChangeShowMarkImage?.(false);
						}
						return !val;
					})
				}
				isMinimizeSpaceChat={isMinimizeSpaceChat}
				isNewMessage={hasNewMessage}
			/>
			{!isMinimizeSpaceChat && (
				<>
					<span onDrop={handleDrop} css={{ width: "100%" }}>
						<MessageInput
							id={currentChatRoomID}
							spaceID={spaceID}
							type={spaceChathatContext.spaceType}
							variant="transparent"
							userList={
								chatUserList ?? [
									{ name: me.name || "", src: me.profileImagePath, userID: Configure.userID }
								]
							}
							myRoomID={spaceInfo?.sceneType === SceneType.MyRoomScene ? channelInfo?.spaceId : undefined}
							isMyRoom={Boolean(
								spaceInfo.sceneType === SceneType.MyRoomScene ||
									(currentRoom?.myRoom && currentRoom?.spaceId === Properties.property.user?.myRoomId)
							)}
							onFilesMessageSent={saveUploadActions}
						/>
					</span>

					<MessageList
						roomID={currentChatRoomID}
						spaceID={spaceID}
						notice={currentRoom?.notice}
						voteNotice={currentRoom?.voteNotice}
						variant="transparent"
						onShowRecentMessage={handleShowRecentMessage}
						isMyRoom={!!currentRoom?.myRoom && currentRoom.spaceId === Properties.property.user?.myRoomId}
						onShowMarkImage={(isShow) => {
							onChangeShowMarkImage?.(isShow);
						}}
					/>
				</>
			)}
		</>
	);
}

function OpenWorld({
	spaceID,
	generalRoomID,
	openWorldName,
	onChangeShowMarkImage
}: {
	spaceID: string;
	generalRoomID: string;
	openWorldName: string;
	onChangeShowMarkImage?: (isShow: boolean) => void;
}) {
	const [currentChatRoomID, setCurrentChatRoomID] = useState(generalRoomID);
	const [isMinimizeSpaceChat, setIsMinimizeSpaceChat] = useState(false);
	const me = usePersistUser("me");

	return (
		<>
			<ChatRoomList
				openWorldName={openWorldName}
				isOpenWorld={true}
				generalRoomID={generalRoomID}
				onSelectChannel={setCurrentChatRoomID}
				toggleMinimizeSpaceChat={() =>
					setIsMinimizeSpaceChat((val) => {
						if (!val) {
							onChangeShowMarkImage?.(false);
						}
						return !val;
					})
				}
				isMinimizeSpaceChat={isMinimizeSpaceChat}
			/>
			<Maybe test={!isMinimizeSpaceChat}>
				<MessageInput
					id={currentChatRoomID}
					spaceID={spaceID}
					type="Space"
					variant="transparent"
					userList={[{ name: me.name || "", src: me.profileImagePath, userID: Configure.userID }]}
					isMyRoom={false}
				/>
				<MessageList
					roomID={currentChatRoomID}
					spaceID={spaceID}
					variant="transparent"
					isMyRoom={false}
					onShowMarkImage={(isShow) => {
						onChangeShowMarkImage?.(isShow);
					}}
				/>
			</Maybe>
		</>
	);
}

/**
 * @description 채팅별 명확한 예외처리를 위한 Confernce 변수 할당
 */
const Conference = Space;
